import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SearchBar from './SearchBar';
import CategoryTabs from './CategoryTabs';
import ToolsGrid from './ToolsGrid';
import { toolsData } from '../data/toolsData';

const ToolsPage = () => {
  const [searchInput, setSearchInput] = useState(''); // User's input
  const [searchTerm, setSearchTerm] = useState(''); // Debounced search term for filtering
  const [currentCategory, setCurrentCategory] = useState('all');

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  // Memoized filtered tools with stable reference
  const filteredTools = useMemo(() => {
    let filtered = toolsData;

    // Filter by category
    if (currentCategory !== 'all') {
      filtered = filtered.filter(tool => tool.category === currentCategory);
    }

    // Filter by search term (debounced)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(tool =>
        tool.title.toLowerCase().includes(searchLower) ||
        tool.description.toLowerCase().includes(searchLower) ||
        tool.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [searchTerm, currentCategory]);

  return (
    <div className="app">
      <div className="stars"></div>
      
      {/* Header Section */}
      <header className="header">
        <div className="container">
          {/* Back Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="back-navigation"
            style={{ marginBottom: '2rem' }}
          >
            <Link 
              to="/" 
              className="back-link"
              style={{
                color: '#64ffda',
                textDecoration: 'none',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'color 0.3s ease'
              }}
            >
              <span>←</span> Back to Home
            </Link>
          </motion.div>

          {/* Title Section */}
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{
              fontSize: '3rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #64ffda 0%, #00ff88 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              Tool Directory
            </h1>
            <p style={{
              fontSize: '1.2rem',
              color: '#b0b0b0',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>
              Explore our curated collection of extraterrestrial productivity tools
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <SearchBar 
              searchTerm={searchInput} 
              onSearchChange={handleSearchChange} 
            />
          </motion.div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main">
        <div className="container">
          {/* Category Tabs */}
          <CategoryTabs 
            currentCategory={currentCategory}
            onCategoryChange={setCurrentCategory}
          />

          {/* Tools Stats */}
          <motion.div
            className="tools-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            style={{
              textAlign: 'center',
              margin: '2rem 0',
              color: '#888',
              fontSize: '1rem'
            }}
          >
            Showing {filteredTools.length} of {toolsData.length} tools
          </motion.div>

          {/* Tools Grid */}
          <ToolsGrid tools={filteredTools} />
        </div>
      </main>
    </div>
  );
};

export default ToolsPage;